import type {
  BillingCycle,
  ChangePlanInput,
  ChangePlanResult,
  CouponValidationResult,
  CreateSubscriptionInput,
  CreateSubscriptionResult,
  InvoiceItem,
  PaymentHistoryItem,
  Plan,
  ProrationPreview,
  RetryCheckoutResult,
  Subscription,
  UsageSummary,
  VerifyPaymentInput,
  VerifyPaymentResult
} from './types';
import { userMessageForErrorCode } from './billingErrors';
import billingClient, { BillingHttpError } from './billingHttpClient';
import { PENDING_CONFIRMATION_MAX_AGE_MS } from './billing.constants';

type BillingApi = {
  getPlans: () => Promise<Plan[]>;
  getMySubscription: () => Promise<Subscription>;
  refreshSubscriptionStatus: () => Promise<Subscription>;
  setMockPaymentMethod: (method: Subscription['paymentMethod']) => Promise<Subscription>;
  createSubscription: (input: CreateSubscriptionInput) => Promise<CreateSubscriptionResult>;
  retryCheckout: () => Promise<RetryCheckoutResult>;
  changePlan: (input: ChangePlanInput) => Promise<ChangePlanResult>;
  cancelSubscription: (cancelAtPeriodEnd: boolean) => Promise<void>;
  cancelScheduledChange: () => Promise<ChangePlanResult>;
  pauseSubscription: () => Promise<void>;
  resumeSubscription: () => Promise<void>;
  verifyPayment: (input: VerifyPaymentInput) => Promise<VerifyPaymentResult>;
  getProrationPreview: (newPlanId: string, cycle: BillingCycle) => Promise<ProrationPreview>;
  validateCoupon: (couponCode: string, planId: string, cycle: BillingCycle) => Promise<CouponValidationResult>;
  getPaymentHistory: (page: number, pageSize: number) => Promise<{ items: PaymentHistoryItem[]; total: number }>;
  getInvoices: (page: number, pageSize: number) => Promise<{ items: InvoiceItem[]; total: number }>;
  getUsageSummary: () => Promise<UsageSummary>;
};

const PENDING_CONFIRMATION_KEY = 'zopeo.billing.pendingConfirmation.v1';

type PendingConfirmation = {
  eventId: string;
  planId: string;
  timestamp: number;
  billingCycle?: BillingCycle;
  paymentMethod?: Subscription['paymentMethod'];
};

const getPendingConfirmation = (): PendingConfirmation | null => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_CONFIRMATION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingConfirmation;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (!parsed.eventId || !parsed.planId || !parsed.timestamp) {
      return null;
    }
    // Discard entries older than PENDING_CONFIRMATION_MAX_AGE_MS.
    // Without this check, a confirmation left in sessionStorage from 45 minutes
    // ago would cause the BillingConfirmationProvider to immediately compute
    // isTimedOut=true and show a false "taking longer than expected" banner,
    // even if the subscription is already ACTIVE.
    if (Date.now() - parsed.timestamp > PENDING_CONFIRMATION_MAX_AGE_MS) {
      try { window.sessionStorage.removeItem(PENDING_CONFIRMATION_KEY); } catch (_) { /* ignore */ }
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const setPendingConfirmation = (payload: PendingConfirmation): void => {
  try {
    window.sessionStorage.setItem(PENDING_CONFIRMATION_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore
  }
};

const clearPendingConfirmation = (): void => {
  try {
    window.sessionStorage.removeItem(PENDING_CONFIRMATION_KEY);
  } catch (error) {
    // ignore
  }
};

const asString = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

const asNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toFreeSubscription = (): Subscription => ({
  id: 'free',
  userId: 'me',
  planId: 'free',
  planName: 'Free',
  billingCycle: 'monthly',
  status: 'ACTIVE',
  paymentMethod: 'free',
  currentPeriodStart: undefined,
  currentPeriodEnd: undefined
});

const mapPlan = (raw: any): Plan => ({
  id: asString(raw.id),
  name: asString(raw.name),
  displayName: asString(raw.displayName || raw.display_name || raw.display),
  description: asString(raw.description),
  priceMonthly: asNumber(raw.priceMonthly ?? raw.price_monthly),
  priceYearly: asNumber(raw.priceYearly ?? raw.price_yearly),
  features: Array.isArray(raw.features)
    ? raw.features.map((f: any) => asString(f))
    : (raw.features && typeof raw.features === 'object' ? Object.values(raw.features).map((f: any) => asString(f)) : []),
  isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
  sortOrder: asNumber(raw.sortOrder ?? raw.sort_order)
});

const mapSubscription = (raw: any): Subscription => ({
  id: asString(raw.id ?? raw.subscriptionId ?? raw.subscription_id),
  userId: asString(raw.userId ?? raw.user_id ?? 'me'),
  planId: asString(raw.planId ?? raw.plan_id ?? raw.plan?.id ?? 'free'),
  planName: asString(raw.planName ?? raw.plan_name ?? raw.plan?.displayName ?? raw.plan?.name ?? 'Free'),
  billingCycle: (asString(raw.billingCycle ?? raw.billing_cycle ?? 'monthly') as BillingCycle) || 'monthly',
  status: asString(raw.status ?? 'ACTIVE') as any,
  paymentMethod: asString(raw.paymentMethod ?? raw.payment_method ?? 'unknown') as any,
  currentPeriodStart: raw.currentPeriodStart ?? raw.current_period_start,
  currentPeriodEnd: raw.currentPeriodEnd ?? raw.current_period_end,
  gracePeriodEnd: raw.gracePeriodEnd ?? raw.grace_period_end,
  cancelAtPeriodEnd: raw.cancelAtPeriodEnd ?? raw.cancel_at_period_end,
  cancelledAt: raw.cancelledAt ?? raw.cancelled_at,
  hasScheduledChanges: raw.hasScheduledChanges ?? raw.has_scheduled_changes,
  pendingPlanId: raw.pendingPlanId ?? raw.pending_plan_id,
  pendingBillingCycle: raw.pendingBillingCycle ?? raw.pending_billing_cycle,
  pendingChangeType: raw.pendingChangeType ?? raw.pending_change_type,
  pendingChangeMethod: raw.pendingChangeMethod ?? raw.pending_change_method,
  pendingEffectiveAt: raw.pendingEffectiveAt ?? raw.pending_effective_at
});

const slicePage = <T>(items: T[], page: number, pageSize: number): { items: T[]; total: number } => {
  const total = items.length;
  const start = Math.max(0, (page - 1) * pageSize);
  const end = Math.min(total, start + pageSize);
  return { items: items.slice(start, end), total };
};

const isNotFound = (error: unknown): boolean => error instanceof BillingHttpError && error.status === 404;

const genClientEventId = (): string => `evt_${Date.now().toString(16)}_${Math.random().toString(16).slice(2, 8)}`;

export const billingApi: BillingApi = {
  getPlans: async () => {
    try {
      const data = await billingClient.get<any>('/api/payments/plans');
      const plans = Array.isArray(data?.plans) ? data.plans : (Array.isArray(data) ? data : []);
      return plans.map(mapPlan).sort((a: Plan, b: Plan) => a.sortOrder - b.sortOrder);
    } catch (error) {
      if (isNotFound(error)) {
        const fallback = await billingClient.get<any>('/api/plans');
        const plans = Array.isArray(fallback?.plans) ? fallback.plans : (Array.isArray(fallback) ? fallback : []);
        return plans.map(mapPlan).sort((a: Plan, b: Plan) => a.sortOrder - b.sortOrder);
      }
      throw error;
    }
  },

  getMySubscription: async () => {
    try {
      const data = await billingClient.get<any>('/api/payments/subscriptions/me');
      if (!data) {
        return toFreeSubscription();
      }
      return mapSubscription(data);
    } catch (error) {
      if (isNotFound(error)) {
        return toFreeSubscription();
      }
      throw error;
    }
  },

  refreshSubscriptionStatus: async () => {
    try {
      const data = await billingClient.get<any>('/api/payments/subscriptions/me');
      if (!data) {
        return toFreeSubscription();
      }
      return mapSubscription(data);
    } catch (error) {
      if (isNotFound(error)) {
        return toFreeSubscription();
      }
      throw error;
    }
  },

  setMockPaymentMethod: async () => {
    // No-op in backend-wired mode; UI still supports this call.
    return billingApi.getMySubscription();
  },

  createSubscription: async (input: CreateSubscriptionInput) => {
    const body = { planId: input.planId, cycle: input.billingCycle, coupon: input.couponCode };
    try {
      const data = await billingClient.post<any>('/api/payments/subscriptions', body);
      return {
        subscriptionId: asString(data.subscriptionId ?? data.subscription_id ?? data.id),
        requiresCheckout: Boolean(data.checkoutKeyId || data.checkout_key_id || data.razorpaySubscriptionId || data.razorpay_subscription_id),
        checkoutKeyId: asString(data.checkoutKeyId ?? data.checkout_key_id),
        razorpaySubscriptionId: asString(data.razorpaySubscriptionId ?? data.razorpay_subscription_id ?? data.subscriptionId ?? data.subscription_id),
        checkoutMessage: asString(data.checkoutMessage ?? data.checkout_message)
      };
    } catch (error) {
      // Fallback to legacy endpoints present in the current backend.
      if (isNotFound(error)) {
        const legacy = await billingClient.post<any>('/api/subscriptions', {
          planId: input.planId,
          billingCycle: input.billingCycle,
          couponCode: input.couponCode
        });
        const razorpaySubscriptionId = asString(legacy.razorpaySubscriptionId);
        const checkoutKeyId = asString(legacy.checkoutKeyId);
        const requiresCheckout = Boolean(razorpaySubscriptionId && checkoutKeyId);
        return {
          subscriptionId: asString(legacy.subscriptionId),
          requiresCheckout,
          checkoutKeyId: checkoutKeyId || undefined,
          razorpaySubscriptionId: razorpaySubscriptionId || undefined,
          checkoutMessage: undefined
        };
      }
      throw error;
    }
  },

  retryCheckout: async () => {
    const data = await billingClient.post<any>('/api/payments/subscriptions/me/retry-checkout', {});
    return {
      requiresCheckout: Boolean(data.requiresCheckout ?? data.requires_checkout ?? data.checkoutKeyId ?? data.checkout_key_id ?? data.razorpaySubscriptionId ?? data.razorpay_subscription_id),
      checkoutKeyId: asString(data.checkoutKeyId ?? data.checkout_key_id) || undefined,
      razorpaySubscriptionId: asString(data.razorpaySubscriptionId ?? data.razorpay_subscription_id) || undefined,
      checkoutMessage: asString(data.checkoutMessage ?? data.checkout_message) || undefined
    };
  },

  changePlan: async (input: ChangePlanInput) => {
    try {
      const data = await billingClient.patch<any>('/api/payments/subscriptions/me/plan', {
        newPlanId: input.newPlanId,
        cycle: input.billingCycle
      });
      return {
        requiresCheckout: Boolean(data.requiresCheckout ?? data.requires_checkout),
        checkoutKeyId: asString(data.checkoutKeyId ?? data.checkout_key_id) || undefined,
        razorpaySubscriptionId: asString(data.razorpaySubscriptionId ?? data.razorpay_subscription_id) || undefined,
        effectiveAt: asString(data.effectiveAt ?? data.effective_at) || undefined,
        checkoutMessage: asString(data.checkoutMessage ?? data.checkout_message) || undefined
      };
    } catch (error) {
      if (isNotFound(error)) {
        const legacy = await billingClient.post<any>('/api/subscriptions/change-plan', {
          newPlanId: input.newPlanId,
          billingCycle: input.billingCycle
        });
        return {
          requiresCheckout: Boolean(legacy.requiresCheckout),
          checkoutKeyId: asString(legacy.checkoutKeyId) || undefined,
          razorpaySubscriptionId: asString(legacy.razorpaySubscriptionId) || undefined,
          effectiveAt: legacy.effectiveAt ? String(legacy.effectiveAt) : undefined,
          checkoutMessage: asString(legacy.checkoutMessage) || undefined
        };
      }
      throw error;
    }
  },

  cancelSubscription: async (cancelAtPeriodEnd: boolean) => {
    try {
      await billingClient.delete<any>('/api/payments/subscriptions/me', cancelAtPeriodEnd ? { reason: 'cancel_at_period_end' } : { reason: 'cancel_now' });
    } catch (error) {
      if (isNotFound(error)) {
        await billingClient.post<any>('/api/subscriptions/cancel', {});
        return;
      }
      throw error;
    }
  },

  cancelScheduledChange: async () => {
    const data = await billingClient.delete<any>('/api/payments/subscriptions/me/scheduled-change');
    return {
      requiresCheckout: Boolean(data.requiresCheckout ?? data.requires_checkout),
      checkoutKeyId: asString(data.checkoutKeyId ?? data.checkout_key_id) || undefined,
      razorpaySubscriptionId: asString(data.razorpaySubscriptionId ?? data.razorpay_subscription_id) || undefined,
      effectiveAt: asString(data.effectiveAt ?? data.effective_at) || undefined,
      checkoutMessage: asString(data.checkoutMessage ?? data.checkout_message) || undefined
    };
  },

  pauseSubscription: async () => {
    try {
      await billingClient.post<void>('/api/payments/subscriptions/me/pause', {});
    } catch (error) {
      if (isNotFound(error)) {
        await billingClient.post<void>('/api/subscriptions/pause', {});
        return;
      }
      throw error;
    }
  },

  resumeSubscription: async () => {
    try {
      await billingClient.post<void>('/api/payments/subscriptions/me/resume', {});
    } catch (error) {
      if (isNotFound(error)) {
        await billingClient.post<void>('/api/subscriptions/resume', {});
        return;
      }
      throw error;
    }
  },

  verifyPayment: async (input: VerifyPaymentInput) => {
    // Include whichever ID Razorpay returned. Current Zopeo billing uses
    // subscription checkout; order verification is rejected server-side until
    // an owned local order/payment mapping exists.
    // JSON.stringify drops undefined fields automatically.
    const payload = {
      razorpay_payment_id: input.razorpay_payment_id,
      razorpay_order_id: input.razorpay_order_id || undefined,
      razorpay_subscription_id: input.razorpay_subscription_id || undefined,
      razorpay_signature: input.razorpay_signature
    };
    const res = await billingClient.post<any>('/api/payments/verify', payload);
    const verified = res?.verified !== undefined ? Boolean(res.verified) : true;
    const eventId = asString(res?.eventId ?? res?.event_id ?? '') || genClientEventId();
    if (!verified) {
      throw new Error('Payment verification failed. Contact support.');
    }
    return { verified, eventId };
  },

  getProrationPreview: async (newPlanId: string, cycle: BillingCycle) => {
    const data = await billingClient.get<any>('/api/payments/subscriptions/me/proration', { newPlanId, cycle });
    return {
      currency: asString(data.currency || 'INR'),
      chargeToday: asNumber(data.chargeToday ?? data.charge_today),
      creditAmount: asNumber(data.creditAmount ?? data.credit_amount ?? 0),
      nextChargeAmount: asNumber(data.nextChargeAmount ?? data.nextBillingAmount ?? data.next_billing_amount),
      nextBillingDate: asString(data.nextBillingDate ?? data.next_billing_date),
      daysRemaining: data.daysRemaining === null || data.daysRemaining === undefined ? null : asNumber(data.daysRemaining),
      currentPlanLabel: asString(data.currentPlanLabel ?? data.currentPlan ?? data.current_plan),
      newPlanLabel: asString(data.newPlanLabel ?? data.newPlan ?? data.new_plan),
      notes: Array.isArray(data.notes) ? data.notes.map((n: any) => asString(n)) : []
    };
  },

  validateCoupon: async (couponCode: string, planId: string, cycle: BillingCycle) => {
    try {
      const data = await billingClient.post<any>('/api/payments/coupons/validate', { couponCode, planId, cycle });
      if (data?.ok === false) {
        return {
          ok: false,
          code: (asString(data.code) as any) || 'COUPON_INVALID',
          message: asString(data.message) || userMessageForErrorCode('COUPON_INVALID'),
          eventId: asString(data.eventId ?? data.event_id) || undefined
        };
      }
      const coupon = data?.coupon || {};
      return {
        ok: true,
        coupon: {
          code: asString(coupon.code),
          discountType: (asString(coupon.discountType) as any) || (asString(coupon.discount_type) as any),
          discountValue: asNumber(coupon.discountValue ?? coupon.discount_value),
          description: asString(coupon.description) || undefined
        },
        discountedAmountMonthly: data.discountedAmountMonthly === undefined ? undefined : asNumber(data.discountedAmountMonthly),
        discountedAmountYearly: data.discountedAmountYearly === undefined ? undefined : asNumber(data.discountedAmountYearly)
      };
    } catch (error) {
      if (error instanceof BillingHttpError && error.status === 404) {
        return { ok: false, code: 'COUPON_INVALID', message: userMessageForErrorCode('COUPON_INVALID') };
      }
      if (error instanceof BillingHttpError && error.status && error.status >= 400 && error.status < 500) {
        return { ok: false, code: 'COUPON_INVALID', message: error.message || userMessageForErrorCode('COUPON_INVALID') };
      }
      throw error;
    }
  },

  getPaymentHistory: async (page: number, pageSize: number) => {
    const data = await billingClient.get<any>('/api/payments/history', { page, pageSize });
    const itemsRaw = Array.isArray(data?.payments) ? data.payments : (Array.isArray(data?.items) ? data.items : []);
    const mapped: PaymentHistoryItem[] = itemsRaw.map((p: any) => ({
      id: asString(p.id ?? p.paymentId ?? p.razorpayPaymentId ?? p.razorpay_payment_id),
      createdAt: asString(p.createdAt ?? p.created_at ?? p.paidAt ?? p.paid_at),
      description: asString(p.description || 'Payment'),
      amount: asNumber(p.amount),
      currency: asString(p.currency || 'INR'),
      status: asString(p.status || 'CREATED') as any,
      method: asString(p.method || 'unknown') as any,
      razorpayPaymentId: asString(p.razorpayPaymentId ?? p.razorpay_payment_id) || undefined,
      invoiceId: asString(p.invoiceId ?? p.invoice_id) || undefined,
      invoicePdfUrl: p.invoicePdfUrl ?? p.invoice_pdf_url ?? null,
      refundedAmount: p.refundedAmount !== undefined ? asNumber(p.refundedAmount) : undefined
    }));

    if (data?.total !== undefined) {
      return { items: mapped, total: asNumber(data.total) };
    }
    return slicePage(mapped, page, pageSize);
  },

  getInvoices: async (page: number, pageSize: number) => {
    try {
      const data = await billingClient.get<any>('/api/payments/invoices', { page, pageSize });
      const rows = Array.isArray(data?.invoices) ? data.invoices : (Array.isArray(data?.items) ? data.items : []);
      const mapped: InvoiceItem[] = rows.map((inv: any) => ({
        id: asString(inv.id ?? inv.invoiceId ?? inv.invoice_id),
        createdAt: asString(inv.createdAt ?? inv.created_at),
        invoiceNumber: asString(inv.invoiceNumber ?? inv.invoice_number),
        amount: asNumber(inv.amount),
        currency: asString(inv.currency || 'INR'),
        status: asString(inv.status || 'DRAFT') as any,
        pdfUrl: inv.pdfUrl ?? inv.pdf_url ?? null
      }));
      const total = data?.total !== undefined ? asNumber(data.total) : mapped.length;
      return { items: mapped, total };
    } catch (error) {
      if (isNotFound(error)) {
        return { items: [], total: 0 };
      }
      throw error;
    }
  },

  getUsageSummary: async () => {
    const data = await billingClient.get<any>('/api/payments/usage/me');
    return {
      billingPeriod: asString(data?.billingPeriod ?? data?.billing_period),
      confirmedBookings: asNumber(data?.confirmedBookings ?? data?.confirmed_bookings),
      billableBookings: asNumber(data?.billableBookings ?? data?.billable_bookings),
      accruedAmount: asNumber(data?.accruedAmount ?? data?.accrued_amount),
      currency: asString(data?.currency || 'INR'),
      planLabel: asString(data?.planLabel ?? data?.plan_label) || null,
      nextInvoiceEstimate: asNumber(data?.nextInvoiceEstimate ?? data?.next_invoice_estimate)
    };
  }
};

export const pendingConfirmationStore = {
  get: getPendingConfirmation,
  set: setPendingConfirmation,
  clear: clearPendingConfirmation
};
